import { useState } from "react";
import { Link } from "react-router-dom";
import { useDataProvider, useNotify } from "ra-core";
import {
  ArrowRight,
  Calendar,
  Check,
  ListPlus,
  Mail,
  MessageSquare,
  Phone,
  Play,
  StickyNote,
  UserPlus,
  Wand2,
  Zap,
} from "lucide-react";
import type { ActionsPayload } from "./types";
import { streamSkillRun } from "@/lib/agenticClient";

const iconFor = {
  email: Mail,
  call: Phone,
  open: ArrowRight,
  complete: Check,
  assign: UserPlus,
  custom: Zap,
  update: Wand2,
  task: ListPlus,
  note: StickyNote,
  schedule: Calendar,
  run_skill: Play,
  prompt: MessageSquare,
} as const;

function entityHref(
  type: NonNullable<ActionsPayload["items"][number]["entity"]>["type"],
  id: string | number,
): string {
  const base = type === "company" ? "companies" : `${String(type)}s`;
  return `/${base}/${id}/show`;
}

export function ActionsBlock({
  payload,
  onPrompt,
}: {
  payload: ActionsPayload;
  onPrompt?: (text: string) => void;
}) {
  return (
    <div className="my-2 w-full rounded-md border bg-background">
      {payload.title && (
        <div className="border-b px-2 py-1 text-xs font-medium">
          {payload.title}
        </div>
      )}
      <ul className="divide-y">
        {payload.items.map((it, i) => (
          <li key={i} className="flex items-start gap-2 px-2 py-1.5">
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium truncate">{it.label}</div>
              {it.reason && (
                <div className="text-[11px] text-muted-foreground truncate">
                  {it.reason}
                </div>
              )}
            </div>
            <div className="flex flex-shrink-0 items-center gap-0.5">
              {it.actions.map((a, j) => (
                <ActionButton
                  key={j}
                  action={a}
                  entity={it.entity}
                  itemLabel={it.label}
                  onPrompt={onPrompt}
                />
              ))}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

type ActionItem = ActionsPayload["items"][number];
type ActionDef = ActionItem["actions"][number];

function ActionButton({
  action,
  entity,
  itemLabel,
  onPrompt,
}: {
  action: ActionDef;
  entity?: ActionItem["entity"];
  itemLabel: string;
  onPrompt?: (text: string) => void;
}) {
  const Icon = iconFor[action.kind] ?? Zap;
  const label = action.label ?? action.kind;
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const baseClass =
    "flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] hover:bg-muted disabled:opacity-50";

  const content = (
    <>
      <Icon className="h-3 w-3" />
      {done ? "Fait" : label}
    </>
  );

  // Navigation: open entity page
  if (action.kind === "open" && entity) {
    return (
      <Link to={entityHref(entity.type, entity.id)} className={baseClass}>
        {content}
      </Link>
    );
  }

  // External link / mailto / tel — generic url passthrough
  if (action.url) {
    return (
      <a
        href={action.url}
        target={action.url.startsWith("http") ? "_blank" : undefined}
        rel="noreferrer"
        className={baseClass}
      >
        {content}
      </a>
    );
  }

  // Prompt action: send a pre-filled user message back to the chat. Used
  // when the agent suggests a multi-field action that it can do itself but
  // needs an explicit go-ahead (e.g. "Crée Lux Control + lie Rémi Lang").
  if (action.kind === "prompt" && action.prompt && onPrompt) {
    const exec = () => {
      if (busy || done) return;
      setBusy(true);
      try {
        onPrompt(action.prompt!.text);
        setDone(true);
      } finally {
        setBusy(false);
      }
    };
    return (
      <button
        className={baseClass}
        onClick={exec}
        disabled={busy || done}
        title={action.prompt.text}
      >
        {content}
      </button>
    );
  }

  // Direct write actions (with confirm).
  // Note: `task` and `note` work even without an entity — a standalone task
  // (e.g. "Nettoyer 129 mails") still belongs to the user and shows up in
  // "Ma journée".
  const hasExecutablePayload =
    (action.kind === "update" && action.patch && entity) ||
    (action.kind === "task" && action.task) ||
    (action.kind === "note" && action.note && entity) ||
    action.kind === "schedule" ||
    action.kind === "run_skill" ||
    // Lenient fallback: a "custom" action with a `task` payload is treated
    // as a task (agents sometimes pick "custom" for "plan today").
    (action.kind === "custom" && (action.task || action.run_skill));

  if (hasExecutablePayload) {
    const exec = async () => {
      const summary = describe(action, itemLabel);
      if (!confirm(`Confirmer : ${summary} ?`)) return;
      setBusy(true);
      try {
        if (action.kind === "update" && action.patch && entity) {
          const resource =
            entity.type === "company" ? "companies" : `${entity.type}s`;
          const previous = await dataProvider
            .getOne(resource, { id: entity.id })
            .catch(() => ({ data: { id: entity.id } }));
          await dataProvider.update(resource, {
            id: entity.id,
            data: action.patch,
            previousData: previous.data ?? { id: entity.id },
          });
        } else if (
          (action.kind === "task" || action.kind === "custom") &&
          action.task
        ) {
          const taskData: Record<string, unknown> = { ...action.task };
          if (entity) {
            const fk =
              entity.type === "contact" ? "contact_id" : `${entity.type}_id`;
            taskData[fk] = entity.id;
          }
          if (!taskData.due_date) {
            taskData.due_date = new Date().toISOString().slice(0, 10);
          }
          await dataProvider.create("tasks", { data: taskData });
        } else if (action.kind === "note" && action.note && entity) {
          const resource =
            entity.type === "deal" ? "dealNotes" : "contactNotes";
          await dataProvider.create(resource, {
            data: {
              text: action.note.text,
              [entity.type === "deal" ? "deal_id" : "contact_id"]: entity.id,
              date: new Date().toISOString(),
            },
          });
        } else if (action.kind === "schedule") {
          if (action.schedule) {
            // Run a skill at a specific time via the agent runtime.
            await dataProvider.create("agentic_scheduled_actions", {
              data: {
                skill_id: action.schedule.skill_id,
                input: action.schedule.input ?? {},
                run_at: action.schedule.when_iso,
              },
            });
          } else if (action.task) {
            // Fallback: schedule = create-a-task-due-at
            const taskData: Record<string, unknown> = { ...action.task };
            if (entity) {
              const fk =
                entity.type === "contact" ? "contact_id" : `${entity.type}_id`;
              taskData[fk] = entity.id;
            }
            if (!taskData.due_date) {
              taskData.due_date = new Date().toISOString().slice(0, 10);
            }
            await dataProvider.create("tasks", { data: taskData });
          } else {
            throw new Error(
              "schedule action requires either `schedule` or `task` payload",
            );
          }
        } else if (
          (action.kind === "run_skill" || action.kind === "custom") &&
          action.run_skill
        ) {
          // Fire-and-forget — drain the SSE stream silently. The user will
          // see the result via the regular skill_runs activity log.
          (async () => {
            try {
              const gen = streamSkillRun(
                action.run_skill!.skill_id,
                action.run_skill!.input ?? {},
              );
              for await (const _ev of gen) {
                /* drain */
              }
            } catch (err) {
              console.error("run_skill failed", err);
            }
          })();
        } else {
          throw new Error("Unsupported action payload");
        }
        setDone(true);
        notify("Action appliquée", { type: "success" });
      } catch (err) {
        notify(`Erreur : ${err instanceof Error ? err.message : String(err)}`, {
          type: "error",
        });
      } finally {
        setBusy(false);
      }
    };
    return (
      <button
        className={baseClass}
        onClick={exec}
        disabled={busy || done}
        title={describe(action, itemLabel)}
      >
        {content}
      </button>
    );
  }

  return (
    <button className={baseClass} disabled>
      {content}
    </button>
  );
}

function describe(action: ActionDef, itemLabel: string): string {
  if (action.kind === "update" && action.patch) {
    const fields = Object.entries(action.patch)
      .map(([k, v]) => `${k}=${v}`)
      .join(", ");
    return `Mettre à jour ${itemLabel} (${fields})`;
  }
  if ((action.kind === "task" || action.kind === "custom") && action.task) {
    return `Créer la tâche "${action.task.name}"`;
  }
  if (action.kind === "note" && action.note) {
    return `Ajouter une note sur ${itemLabel}`;
  }
  if (action.kind === "schedule") {
    if (action.schedule) {
      return `Planifier ${action.schedule.skill_id} pour ${action.schedule.when_iso}`;
    }
    if (action.task) {
      return `Planifier la tâche "${action.task.name}"`;
    }
  }
  if (action.kind === "run_skill" && action.run_skill) {
    return `Lancer le skill ${action.run_skill.skill_id}`;
  }
  if (action.kind === "custom" && action.run_skill) {
    return `Lancer le skill ${action.run_skill.skill_id}`;
  }
  return action.label ?? action.kind;
}
